import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FlyoutService } from '../flyout.service';
import { CurrentContextService } from '../../core/current-context.service';
import { combineLatest, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { AccountService } from '../../account/account.service';
import { BasicAccount } from '../../back-office/account.model';
import { SnackBarService } from '../../core/snack-bar.service';
import { ConfirmationDialogService } from '../../shared/confirmation-dialog/confirmation-dialog.service';

@Component({
  selector: 'app-flyout-copy-brand-settings',
  templateUrl: './flyout-copy-brand-settings.component.html',
  styleUrl: './flyout-copy-brand-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutCopyBrandSettingsComponent implements OnInit, OnDestroy {
  private customerAccounts: BasicAccount[] = [];
  private allAccounts: BasicAccount[] = [];

  public shouldCopyDesign: boolean = true;
  public shouldCopyText: boolean;
  public shouldReplaceTextVariables: boolean;
  public allCurrentSitedIds: number[] = [];
  public currentContextSubscription: Subscription;
  public selectAll: boolean = false;
  public selectedAccountsString = '';
  public currentAccountName = '';
  public copyDisabled = true;

  siteIdsControl = new FormControl([]);
  isAdmin = false;
  showAllAccounts = false;
  isLoading = false;
  currentAccounts: BasicAccount[] = [];

  constructor(
    private translateService: TranslateService,
    private flyoutService: FlyoutService,
    private readonly currentContextService: CurrentContextService,
    private accountService: AccountService,
    private snackbarService: SnackBarService,
    private confirmationDialogService: ConfirmationDialogService,
  ) {}

  ngOnInit(): void {
    combineLatest({
      currentContext: this.currentContextService.currentContextBS,
      accounts: this.accountService.getAllAccounts(),
    }).subscribe(({ currentContext, accounts }) => {
      this.isAdmin = currentContext.user.isAdmin;
      if (currentContext && currentContext.user.siteIds) {
        this.allCurrentSitedIds = currentContext.user.siteIds.filter(
          (siteId) => siteId !== currentContext.account.siteId,
        );
      }
      const customerCurrentSiteIds =
        currentContext.customer?.siteIds.filter((siteId) => siteId !== currentContext.account.siteId) ?? [];

      this.allAccounts = accounts.filter((account) => this.allCurrentSitedIds.includes(account.siteId));

      this.customerAccounts = accounts.filter((account) => customerCurrentSiteIds.includes(account.siteId));
      this.currentAccounts = this.customerAccounts;

      this.currentAccountName = currentContext.account.name;
    });

    this.siteIdsControl.valueChanges.subscribe((updatedValue) => {
      const selectedAccounts = this.allAccounts.filter((account) => this.siteIdsControl.value.includes(account.siteId));
      this.selectedAccountsString = selectedAccounts
        .reverse()
        .slice(0, 3)
        .map((account) => account.name)
        .join(', ');

      if (this.selectedAccountsString.length > 50) {
        this.selectedAccountsString = this.selectedAccountsString.slice(0, 50) + '...';
      }

      this.selectAll = this.siteIdsControl.value.length === this.currentAccounts.length;
    });
  }

  public closeDialog() {
    this.flyoutService.close();
  }

  copyBrandSettings() {
    this.confirmationDialogService
      .confirm({
        title: 'confirmationDialog.confirmationTitle',
        text: 'settings.customization.copy-brand-settings.confirmation',
      })
      .subscribe((confirmed) => (confirmed === true ? this.updateAccounts() : null));
  }

  private updateAccounts() {
    this.isLoading = true;
    this.flyoutService
      .copyBrandSettings(
        this.shouldCopyDesign,
        this.shouldCopyText,
        this.shouldReplaceTextVariables,
        this.siteIdsControl.value,
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.snackbarService.success('settings.customization.copy-brand-settings.success');
        },
        error: () => {
          this.isLoading = false;
          this.snackbarService.error('settings.customization.copy-brand-settings.error');
        },
      });
  }

  ngOnDestroy(): void {
    if (this.currentContextSubscription) {
      this.currentContextSubscription.unsubscribe();
    }
  }

  public toggleSelectAll() {
    if (this.selectAll) {
      this.siteIdsControl.setValue(this.currentAccounts.map((account) => account.siteId));
    } else {
      this.siteIdsControl.setValue([]);
    }
  }

  onShowAllAccounts() {
    this.siteIdsControl.setValue([]);
    this.selectAll = false;
    if (this.showAllAccounts) {
      this.currentAccounts = this.allAccounts;
    } else {
      this.currentAccounts = this.customerAccounts;
    }
  }

  checkIfShouldDisableCopyButton() {
    this.copyDisabled = !((this.shouldCopyDesign || this.shouldCopyText) && this.siteIdsControl.value.length > 0);
  }
}
